import { apiClient, idempotentConfig, unwrap } from './client';
import { runPathId } from './runPath';
import type { ApiEnvelope } from './types';

/** OL-26 slice 2: case actions and per-step room status (PROPOSAL-ol-26-case-actions.md). */
export interface CaseAction {
  actionId: string;
  action:
    | 'OVERRIDE'
    | 'OVERRIDE_PROPOSED'
    | 'OVERRIDE_REJECTED'
    | 'REDO'
    | 'REREQUEST'
    | 'FOUR_EYES_PROPOSED'
    | 'FOUR_EYES_CONFIRMED'
    | 'FOUR_EYES_DISAGREED'
    | 'FOUR_EYES_ESCALATED'
    | 'FOUR_EYES_RESOLVED';
  runId?: string | null;
  entityId: string;
  targetRef?: string | null;
  finding?: 'ACCEPTED' | 'REJECTED' | null;
  evidenceHashes: string[];
  outcome?: string | null;
  result?: Record<string, unknown> | null;
  reason: string;
  actor: string;
  createdAt: string;
}

/** An override proposal (owner ruling 9). Only APPROVED is a finding in force. */
export interface CaseOverride {
  overrideId: string;
  runId: string;
  entityId: string;
  finding: 'ACCEPTED' | 'REJECTED';
  evidenceHashes: string[];
  reason: string;
  status: 'PROPOSED' | 'APPROVED' | 'REJECTED';
  proposedBy: string;
  proposedBySub: string;
  proposedAt: string;
  reviewedBy?: string | null;
  reviewedBySub?: string | null;
  reviewNote?: string | null;
  decidedAt?: string | null;
}

export interface RoomStep {
  taskId: string;
  kind: string;
  title: string;
  status: string;
  updatedAt?: string | null;
  reopenCount: number;
}

export interface RoomSteps {
  inviteId: string;
  inviteStatus: string;
  steps: RoomStep[];
}

const enc = encodeURIComponent;

export async function listCaseActions(runId: string): Promise<CaseAction[]> {
  const { data } = await apiClient.get<ApiEnvelope<{ actions: CaseAction[] }>>(`/v1/runs/${runPathId(runId)}/case-actions`);
  return unwrap(data).actions;
}

export async function overrideEvidence(
  runId: string,
  evidenceHashes: string[],
  finding: 'ACCEPTED' | 'REJECTED',
  reason: string,
): Promise<CaseAction> {
  const { data } = await apiClient.post<ApiEnvelope<CaseAction>>(
    `/v1/runs/${runPathId(runId)}/overrides`,
    { evidenceHashes, finding, reason },
    idempotentConfig(),
  );
  return unwrap(data);
}

export async function listOverrides(runId: string): Promise<CaseOverride[]> {
  const { data } = await apiClient.get<ApiEnvelope<{ overrides: CaseOverride[] }>>(`/v1/runs/${runPathId(runId)}/overrides`);
  return unwrap(data).overrides;
}

export async function reviewOverride(
  runId: string,
  overrideId: string,
  answer: 'approve' | 'reject',
  note?: string,
): Promise<CaseOverride> {
  const { data } = await apiClient.post<ApiEnvelope<CaseOverride>>(
    `/v1/runs/${runPathId(runId)}/overrides/${enc(overrideId)}/${answer}`,
    { note: note || undefined },
    idempotentConfig(),
  );
  return unwrap(data);
}

export async function redoStep(runId: string, nodeId: string, reason: string): Promise<CaseAction> {
  const { data } = await apiClient.post<ApiEnvelope<CaseAction>>(
    `/v1/runs/${runPathId(runId)}/redo`,
    { nodeId, reason },
    idempotentConfig(),
  );
  return unwrap(data);
}

export async function listRoomSteps(inviteId: string): Promise<RoomSteps> {
  const { data } = await apiClient.get<ApiEnvelope<RoomSteps>>(`/v1/dataroom/invites/${enc(inviteId)}/steps`);
  return unwrap(data);
}

export async function rerequestStep(
  inviteId: string,
  taskId: string,
  reason: string,
  applicantMessage?: string,
): Promise<CaseAction> {
  const { data } = await apiClient.post<ApiEnvelope<CaseAction>>(
    `/v1/dataroom/invites/${enc(inviteId)}/steps/${enc(taskId)}/rerequest`,
    { reason, applicantMessage: applicantMessage || undefined },
    idempotentConfig(),
  );
  return unwrap(data);
}
